import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Avatar,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import UserLocations from './UserLocations';
import AddLocationForm from './AddLocationForm';


interface LocationDialogProps {
  open: boolean;
  onClose: () => void;
  user: any;
}

const LocationDialog: React.FC<LocationDialogProps> = ({ open, onClose, user }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editLocation, setEditLocation] = useState<any>(null);
  if (!user) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          overflow: 'hidden',
          background: 'linear-gradient(145deg, #ffffff 0%, #f5f7fa 100%)',
        },
      }}
      TransitionProps={{
        timeout: 400,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: 'relative',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
          color: 'white',
          p: 4,
          pb: 6,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            right: '-20%',
            width: '300px',
            height: '300px',
            background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
            borderRadius: '50%',
          },
        }}
      >

        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: 'white',
            backgroundColor: 'rgba(255,255,255,0.2)',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.3)',
              transform: 'rotate(90deg) scale(1.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* User Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 60,
              height: 60,
              background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
              color: '#764ba2',
              fontWeight: 700,
              fontSize: '1.5rem',
              border: '3px solid rgba(255,255,255,0.3)',
            }}
          >
            {user.first_name?.[0]}{user.last_name?.[0]}
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              {user.first_name} {user.last_name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationOnIcon sx={{ fontSize: 18 }} />
              <Typography variant="body2">Location History</Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 0, mt: -3 }}>
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: '24px 24px 0 0',
            p: 3,
            minHeight: '300px',
          }}
        >
          {/* Add Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 2 }}>
            <Button
              variant="contained"
              color={showAddForm ? 'secondary' : 'primary'}
              onClick={() => {
                setEditLocation(null);
                setShowAddForm(!showAddForm);
              }}
              sx={{
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}
            >
              {showAddForm ? 'Back to List' : 'Add Location'}
            </Button>
          </Box>

          {/* Conditional View */}
          {showAddForm ? (
            <AddLocationForm
              userId={user.id}
              location={editLocation}
              onSuccess={() => {
                setShowAddForm(false);
              }}
            />
          ) : (
            <UserLocations userId={user.id} onEdit={(loc) => {
              setEditLocation(loc);
              setShowAddForm(true);
            }} />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LocationDialog;
